import React, { useEffect, useState, createContext, useContext } from 'react';
type Language = 'en' | 'ru' | 'ua';
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getPath: (path: string) => string;
};
const translations = {
  en: {
    // Navigation
    'nav.getStarted': 'Free Consultation',
    'nav.home': 'Home',
    'nav.services': 'Pricing',
    'nav.referral': 'Referral Program',
    'nav.privacy': 'Privacy Policy',
    'nav.terms': 'Terms of Service',
    // Cookie Banner
    'cookie.title': 'We use cookies',
    'cookie.description': 'We use cookies to improve your experience on our site, analyze traffic, and personalize content.',
    'cookie.accept': 'Accept All',
    'cookie.decline': 'Decline',
    'cookie.learnMore': 'Learn More',
    // Hero
    'hero.badge': 'Professional IT Studio',
    'hero.title': 'We create modern websites',
    'hero.titleHighlight': 'for your business',
    'hero.subtitle': 'We help businesses launch modern websites with a clear structure, strong presentation, and a focus on results.',
    'hero.cta1': 'Pricing',
    'hero.cta2': 'Get a Free Consultation',
    'hero.referral': 'Referral Program',
    'hero.stat1.value': '7+',
    'hero.stat1.suffix': '',
    'hero.stat1.label': 'years creating IT solutions',
    'hero.stat2.value': '499+',
    'hero.stat2.suffix': '',
    'hero.stat2.label': 'websites built',
    'hero.stat3.value': '99.9%',
    'hero.stat3.suffix': '',
    'hero.stat3.label': 'positive reviews',
    // Pricing
    'pricing.title': 'Website',
    'pricing.titleHighlight': 'Pricing',
    'pricing.subtitle': 'Choose a website format for your business needs. We will help you determine the right solution, structure, and content for your project.',
    'pricing.demo.title': 'Initial Website Concept',
    'pricing.demo.price': '$0',
    'pricing.demo.label': 'FREE',
    'pricing.demo.desc': 'We discuss your project, recommend the most suitable website format, and prepare a preliminary mockup for your website.',
    'pricing.demo.b1': 'Project and website format analysis',
    'pricing.demo.b2': 'Recommendations on structure and presentation',
    'pricing.demo.b3': 'Free mockup',
    'pricing.demo.cta': 'Discuss Your Project',
    'pricing.single.title': 'Single-Page Website',
    'pricing.single.price': 'from $590',
    'pricing.single.period': '',
    'pricing.single.desc': 'A modern single-page website for service businesses, company presentation, personal branding, or advertising campaigns.',
    'pricing.single.b1': 'Single-page website',
    'pricing.single.b2': 'Custom design',
    'pricing.single.b3': 'Convenient navigation and logical structure',
    'pricing.single.b4': 'SEO optimization',
    'pricing.single.cta': 'Discuss Your Project',
    'pricing.multi.title': 'Multi-Page Website',
    'pricing.multi.price': 'from $790',
    'pricing.multi.period': '',
    'pricing.multi.desc': 'A complete website for companies that need to present their services, build trust, and provide a clear structure for clients.',
    'pricing.multi.b1': 'Multi-page website',
    'pricing.multi.b2': 'Custom design',
    'pricing.multi.b3': 'Convenient navigation and logical structure',
    'pricing.multi.b4': 'SEO optimization',
    'pricing.multi.b5': '',
    'pricing.multi.cta': 'Discuss Your Project',
    'pricing.note': '',
    // How It Works
    'how.title': 'How do we',
    'how.titleHighlight': 'create websites?',
    'how.step1.title': 'Tell us about your business',
    'how.step1.note': 'We learn about your goals, services, and audience to understand which type of website fits your project best.',
    'how.step2.title': 'We offer the best solution',
    'how.step2.note': 'We recommend the right website format, structure, presentation, and functionality based on your goals and budget.',
    'how.step3.title': 'We agree on the concept and details',
    'how.step3.note': 'We discuss the style, sections, pages, and key elements of the website. If needed, we can prepare a free mockup.',
    'how.step4.title': 'We build your website',
    'how.step4.note': 'Once the concept is approved, we start building the website and finalize every detail.',
    'how.step5.title': 'We connect your domain and launch the website',
    'how.step5.note': 'Your website goes live and starts generating inquiries.',
    'how.note': '',
    // Why Choose Us
    'why.title': 'Why Choose Us?',
    'why.item1.title': 'Modern Solutions',
    'why.item1.desc': 'We create modern websites with strong visual design, clear structure, and a user-friendly experience for your clients.',
    'why.item2.title': 'Individual Approach',
    'why.item2.desc': 'We consider your business, goals, and visual preferences to create a website that truly fits your brand.',
    'why.item3.title': 'Guaranteed Results',
    'why.item3.desc': 'We carefully handle every stage of the project, make the necessary revisions, and deliver a final result that meets your expectations.',
    // FAQ
    'faq.title': 'Frequently Asked',
    'faq.titleHighlight': 'Questions',
    'faq.subtitle': 'Everything you need to know about working with our team',
    'faq.q1': 'How does a project start?',
    'faq.a1': 'First, we study your business, goals, and website requirements. Then we offer the optimal format, structure, and approach to project implementation.',
    'faq.q2': 'What types of websites do you create?',
    'faq.a2': 'We develop single-page and multi-page websites for companies, specialists, services, and brands. We choose a solution based on business format, goals, and audience.',
    'faq.q3': 'Do you help determine which type of website is needed?',
    'faq.a3': "Yes. If you don't yet have a clear understanding of the structure or format, we will suggest which solution works best in terms of presentation, usability, and business goals.",
    'faq.q4': 'Can I request changes during the process?',
    'faq.a4': 'Yes, during the work we approve important stages and make revisions to the structure, texts, sections, and visual presentation within the project.',
    'faq.q5': 'Do you work with clients worldwide?',
    'faq.a5': 'Yes. We work remotely and manage projects for clients from different countries — from the first discussion to handing over the finished website.',
    // Testimonials
    'testimonials.title': 'Reviews',
    'testimonials.titleHighlight': '',
    'testimonials.subtitle': 'Reviews from clients we helped launch a modern business website',
    'testimonials.r1.name': 'Sarah',
    'testimonials.r1.text': "I liked that the team didn't just focus on design — they really helped us understand what kind of website the business needed. They explained the structure clearly, proposed a solid solution, and delivered everything professionally.",
    'testimonials.r1.business': 'Beauty specialist',
    'testimonials.r2.name': 'Alex',
    'testimonials.r2.text': 'It felt like we were working not just with contractors, but with people who truly understand presentation, structure, and how to build trust with clients. The website turned out modern, clear, and professional.',
    'testimonials.r2.business': 'Local handyman',
    'testimonials.r3.name': 'James',
    'testimonials.r3.text': "The team's biggest strength is their approach. They helped us not only with the visual side of the website, but also with the overall concept, structure, and the best way to present our services.",
    'testimonials.r3.business': 'Business consultant',
    // EN Consultation
    'consultation.title': 'Free Consultation',
    'consultation.titleHighlight': '',
    'consultation.subtitle': "Tell us about your business and goals — we'll recommend the website format that suits you best, suggest the right structure, and offer the best solution for your project.",
    'consultation.bottomNote': 'After your request, we will contact you and discuss the best option for launching your project.',
    'consultation.benefit1': 'Free preview',
    'consultation.benefit2': 'Fast delivery',
    'consultation.benefit3': 'No commitment',
    'consultation.name': 'Name',
    'consultation.namePlaceholder': 'Your name',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'your@email.com',
    'consultation.desc': 'Project description',
    'consultation.descPlaceholder': 'Tell us about your business and what kind of website you need...',
    'consultation.cta': 'Send Request',
    'consultation.note': "No commitment. If you don't like the website — you don't pay.",
    'consultation.sending': 'Sending...',
    'consultation.or': 'or talk to our team directly',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Services',
    'footer.company': 'Company',
    'footer.legal': 'Legal',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved.',
    'footer.aboutLink': 'About IT Monsters',
    // Stats (kept for backwards compat)
    'stats.websites': 'Websites Launched',
    'stats.clients': 'Happy Clients',
    'stats.years': 'Years Experience',
    'stats.title': 'Our',
    'stats.titleHighlight': 'Impact',
    'stats.subtitle': 'Delivering results for businesses worldwide',
    // DD Page
    'dd.hero.title': 'IT Monsters',
    'dd.hero.titleHighlight': 'Digital Studio',
    'dd.hero.subtitle': 'We create websites, develop applications, and launch advertising campaigns. A full cycle of digital services for your business growth.',
    'dd.hero.cta': 'Get a Free Consultation',
    'dd.services.title': 'Our',
    'dd.services.titleHighlight': 'Services',
    'dd.services.websites.title': 'Website Development',
    'dd.services.websites.desc': 'Modern, responsive websites with clear structure, custom design, and SEO optimization for your business.',
    'dd.services.websites.cta': 'Learn More',
    'dd.services.apps.title': 'App Development',
    'dd.services.apps.desc': 'Mobile and web applications with intuitive interfaces, reliable architecture, and smooth user experience.',
    'dd.services.apps.cta': 'Learn More',
    'dd.services.ads.title': 'Advertising Launch',
    'dd.services.ads.desc': 'Targeted advertising campaigns on Google, Facebook, and Instagram to attract clients and grow your business.',
    'dd.services.ads.cta': 'Learn More',
    'dd.faq.title': 'Why Choose',
    'dd.faq.titleHighlight': 'Our Studio?',
    'dd.faq.q1': 'Why is IT Monsters better than other studios?',
    'dd.faq.a1': 'We combine deep technical expertise with a strong focus on design and business results. Every project gets individual attention — from concept to launch.',
    'dd.faq.q2': 'What makes your approach different?',
    'dd.faq.a2': "We don't just execute tasks — we help you find the best solution. We analyze your business, audience, and goals before writing a single line of code.",
    'dd.faq.q3': 'Do you work with startups and small businesses?',
    'dd.faq.a3': 'Yes. We work with businesses of all sizes — from solo entrepreneurs to established companies. We adapt our approach and budget to fit your needs.',
    'dd.faq.q4': 'What technologies do you use?',
    'dd.faq.a4': 'We use modern technologies: React, TypeScript, Node.js, and more. We choose the best tools for each specific project to ensure performance and scalability.',
    'dd.faq.q5': 'How long does a typical project take?',
    'dd.faq.a5': 'A single-page website takes 1–2 weeks, a multi-page website 2–4 weeks, and applications 4–8 weeks depending on complexity. We always agree on timelines upfront.'
  },
  ru: {
    // Navigation
    'nav.getStarted': 'Бесплатная консультация',
    'nav.home': 'Главная',
    'nav.services': 'Цены',
    'nav.referral': 'Реферальная программа',
    'nav.privacy': 'Политика конфиденциальности',
    'nav.terms': 'Условия использования',
    // Cookie Banner
    'cookie.title': 'Мы используем cookies',
    'cookie.description': 'Мы используем cookies для улучшения вашего опыта на сайте, анализа трафика и персонализации контента.',
    'cookie.accept': 'Принять все',
    'cookie.decline': 'Отклонить',
    'cookie.learnMore': 'Подробнее',
    // Hero
    'hero.badge': 'Профессиональная IT-студия',
    'hero.title': 'Создаём современные сайты',
    'hero.titleHighlight': 'для вашего бизнеса',
    'hero.subtitle': 'Помогаем бизнесу запускать современные сайты с продуманной структурой, понятной подачей и акцентом на результат.',
    'hero.cta1': 'Стоимость услуг',
    'hero.cta2': 'Заказать консультацию',
    'hero.referral': 'Реферальная программа',
    'hero.stat1.value': '7+',
    'hero.stat1.suffix': '',
    'hero.stat1.label': 'лет создаем it решения',
    'hero.stat2.value': '499+',
    'hero.stat2.suffix': '',
    'hero.stat2.label': 'сайтов сделано',
    'hero.stat3.value': '99.9%',
    'hero.stat3.suffix': '',
    'hero.stat3.label': 'позитивных отзывов',
    // Pricing
    'pricing.title': 'Стоимость',
    'pricing.titleHighlight': 'создания сайта',
    'pricing.subtitle': 'Выберите формат сайта под задачи вашего бизнеса. Поможем определить подходящее решение, структуру и наполнение проекта.',
    'pricing.demo.title': 'Предварительная концепция',
    'pricing.demo.price': '$0',
    'pricing.demo.label': 'БЕСПЛАТНО',
    'pricing.demo.desc': 'Обсудим ваш проект, подскажем оптимальный формат сайта и подготовим предварительный макет вашего сайта.',
    'pricing.demo.b1': 'Анализ задачи и формата сайта',
    'pricing.demo.b2': 'Рекомендации по структуре и подаче',
    'pricing.demo.b3': 'Бесплатный макет',
    'pricing.demo.cta': 'Обсудить проект',
    'pricing.single.title': 'Одностраничный сайт',
    'pricing.single.price': 'от $590',
    'pricing.single.period': '',
    'pricing.single.desc': 'Современный одностраничный сайт для услуг, презентации компании, личного бренда или запуска рекламы.',
    'pricing.single.b1': 'Одностраничный сайт',
    'pricing.single.b2': 'Индивидуальный дизайн',
    'pricing.single.b3': 'Удобная навигация и логичная структура',
    'pricing.single.b4': 'SEO оптимизация',
    'pricing.single.cta': 'Обсудить проект',
    'pricing.multi.title': 'Многостраничный сайт',
    'pricing.multi.price': 'от $790',
    'pricing.multi.period': '',
    'pricing.multi.desc': 'Полноценный сайт для компаний, которым важно представить услуги, усилить доверие и выстроить удобную структуру для клиентов.',
    'pricing.multi.b1': 'Многостраничный сайт',
    'pricing.multi.b2': 'Индивидуальный дизайн',
    'pricing.multi.b3': 'Удобная навигация и логичная структура',
    'pricing.multi.b4': 'SEO оптимизация',
    'pricing.multi.b5': '',
    'pricing.multi.cta': 'Обсудить проект',
    'pricing.note': '',
    // How It Works
    'how.title': 'Как мы создаём',
    'how.titleHighlight': 'сайты?',
    'how.step1.title': 'Расскажите о вашем бизнесе',
    'how.step1.note': 'Изучаем ваши задачи, услуги, аудиторию и понимаем, какой сайт подойдёт именно вашему проекту',
    'how.step2.title': 'Мы предлагаем оптимальное решение',
    'how.step2.note': 'Подскажем подходящий формат сайта, структуру, подачу и функционал с учётом ваших целей и бюджета',
    'how.step3.title': 'Согласовываем концепцию и детали',
    'how.step3.note': 'Обсуждаем стиль, блоки, страницы и важные элементы. При необходимости можем подготовить бесплатный макет',
    'how.step4.title': 'Создаём сайт для вас',
    'how.step4.note': 'После согласования начинаем работу над проектом и доводим сайт до готового результата',
    'how.step5.title': 'Подключаем домен и публикуем сайт',
    'how.step5.note': 'Сайт начинает работать и принимать заявки',
    'how.note': '',
    // Why Choose Us
    'why.title': 'Почему выбирают нас?',
    'why.item1.title': 'Современные решения',
    'why.item1.desc': 'Создаём сайты с актуальной визуальной подачей, продуманной структурой и удобным интерфейсом для вашего бизнеса и клиентов.',
    'why.item2.title': 'Индивидуальный подход',
    'why.item2.desc': 'Учитываем особенности вашего бизнеса, задачи проекта и желаемый стиль, чтобы сайт получился точным и уместным.',
    'why.item3.title': 'Гарантия результата',
    'why.item3.desc': 'Работаем внимательно на каждом этапе проекта, вносим необходимые правки и доводим сайт до результата, который вас полностью устроит.',
    // FAQ
    'faq.title': 'Частые',
    'faq.titleHighlight': 'вопросы',
    'faq.subtitle': 'Всё, что нужно знать о работе с нами',
    'faq.q1': 'С чего начинается работа над проектом?',
    'faq.a1': 'Сначала мы изучаем ваш бизнес, задачи и пожелания к сайту. После этого предлагаем оптимальный формат, структуру и подход к реализации проекта.',
    'faq.q2': 'Какие сайты вы создаёте?',
    'faq.a2': 'Мы разрабатываем одностраничные и многостраничные сайты для компаний, специалистов, услуг и брендов. Подбираем решение под формат бизнеса, цели и аудиторию.',
    'faq.q3': 'Помогаете ли вы определить, какой сайт нужен?',
    'faq.a3': 'Да. Если у вас пока нет точного понимания структуры или формата, мы подскажем, какое решение подойдёт лучше всего с точки зрения подачи, удобства и задач бизнеса.',
    'faq.q4': 'Можно ли вносить правки в процессе?',
    'faq.a4': 'Да, в процессе работы мы согласовываем важные этапы и вносим правки по структуре, текстам, блокам и визуальной подаче в рамках проекта.',
    'faq.q5': 'Вы работаете с клиентами по всему миру?',
    'faq.a5': 'Да. Мы работаем дистанционно и сопровождаем проекты для клиентов из разных стран — от первого обсуждения до передачи готового сайта.',
    // Testimonials
    'testimonials.title': 'Отзывы',
    'testimonials.titleHighlight': '',
    'testimonials.subtitle': 'Отзывы клиентов, которым мы помогли запустить современный сайт для бизнеса',
    'testimonials.r1.name': 'Анастасия',
    'testimonials.r1.text': 'Понравилось, что команда не просто делает дизайн, а действительно помогает разобраться, какой сайт нужен бизнесу. Всё объяснили по структуре, предложили понятное решение и аккуратно реализовали проект.',
    'testimonials.r1.business': 'Мастер бровей',
    'testimonials.r2.name': 'Дмитрий',
    'testimonials.r2.text': 'Было ощущение, что мы работаем не просто с исполнителями, а с людьми, которые понимают подачу, логику и доверие со стороны клиента. Сайт получился современным и понятным.',
    'testimonials.r2.business': 'Мастер по сантехнике',
    'testimonials.r3.name': 'Сергей',
    'testimonials.r3.text': 'Сильная сторона команды — в подходе. Нам помогли не только с визуальной частью, но и с общей концепцией сайта, структурой и тем, как лучше показать услуги.',
    'testimonials.r3.business': 'Психолог',
    // RU Consultation
    'consultation.title': 'Бесплатная консультация',
    'consultation.titleHighlight': '',
    'consultation.subtitle': 'Расскажите о вашем бизнесе и задачах — мы подскажем, какой сайт подойдёт именно вам, предложим структуру, формат и оптимальное решение под ваш проект.',
    'consultation.bottomNote': 'После заявки мы свяжемся с вами и обсудим оптимальный вариант запуска проекта.',
    'consultation.benefit1': 'Бесплатный просмотр',
    'consultation.benefit2': 'Быстрая доставка',
    'consultation.benefit3': 'Без обязательств',
    'consultation.name': 'Имя',
    'consultation.namePlaceholder': 'Ваше имя',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'ваш@email.com',
    'consultation.desc': 'Описание проекта',
    'consultation.descPlaceholder': 'Расскажите о вашем бизнесе и о том, какой сайт вам нужен…',
    'consultation.cta': 'Отправить заявку',
    'consultation.note': 'Без обязательств. Если сайт не понравится — вы не платите.',
    'consultation.sending': 'Отправка...',
    'consultation.or': 'или напишите нам напрямую',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Услуги',
    'footer.company': 'Компания',
    'footer.legal': 'Юридическое',
    'footer.contact': 'Контакты',
    'footer.rights': 'Все права защищены.',
    'footer.aboutLink': 'О компании',
    // Stats
    'stats.websites': 'Сайтов запущено',
    'stats.clients': 'Довольных клиентов',
    'stats.years': 'Лет опыта',
    'stats.title': 'Наш',
    'stats.titleHighlight': 'Вклад',
    'stats.subtitle': 'Результаты для бизнеса по всему миру',
    // DD Page
    'dd.hero.title': 'IT Monsters',
    'dd.hero.titleHighlight': 'Диджитал Студия',
    'dd.hero.subtitle': 'Создаём сайты, разрабатываем приложения и запускаем рекламу. Полный цикл digital-услуг для роста вашего бизнеса.',
    'dd.hero.cta': 'Бесплатная консультация',
    'dd.services.title': 'Наши',
    'dd.services.titleHighlight': 'Услуги',
    'dd.services.websites.title': 'Разработка сайтов',
    'dd.services.websites.desc': 'Современные адаптивные сайты с продуманной структурой, индивидуальным дизайном и SEO-оптимизацией для вашего бизнеса.',
    'dd.services.websites.cta': 'Подробнее',
    'dd.services.apps.title': 'Разработка приложений',
    'dd.services.apps.desc': 'Мобильные и веб-приложения с интуитивным интерфейсом, надёжной архитектурой и удобным пользовательским опытом.',
    'dd.services.apps.cta': 'Подробнее',
    'dd.services.ads.title': 'Запуск рекламы',
    'dd.services.ads.desc': 'Таргетированная реклама в Google, Facebook и Instagram для привлечения клиентов и роста вашего бизнеса.',
    'dd.services.ads.cta': 'Подробнее',
    'dd.faq.title': 'Почему выбирают',
    'dd.faq.titleHighlight': 'нашу студию?',
    'dd.faq.q1': 'Чем IT Monsters лучше других студий?',
    'dd.faq.a1': 'Мы сочетаем глубокую техническую экспертизу с сильным фокусом на дизайн и бизнес-результаты. Каждый проект получает индивидуальное внимание — от концепции до запуска.',
    'dd.faq.q2': 'Чем отличается ваш подход?',
    'dd.faq.a2': 'Мы не просто выполняем задачи — мы помогаем найти лучшее решение. Анализируем ваш бизнес, аудиторию и цели до начала работы.',
    'dd.faq.q3': 'Работаете ли вы со стартапами и малым бизнесом?',
    'dd.faq.a3': 'Да. Мы работаем с бизнесом любого масштаба — от индивидуальных предпринимателей до крупных компаний. Адаптируем подход и бюджет под ваши потребности.',
    'dd.faq.q4': 'Какие технологии вы используете?',
    'dd.faq.a4': 'Мы используем современные технологии: React, TypeScript, Node.js и другие. Выбираем лучшие инструменты для каждого конкретного проекта.',
    'dd.faq.q5': 'Сколько времени занимает типичный проект?',
    'dd.faq.a5': 'Одностраничный сайт — 1–2 недели, многостраничный — 2–4 недели, приложения — 4–8 недель в зависимости от сложности. Сроки всегда согласовываем заранее.'
  },
  ua: {
    // Navigation
    'nav.getStarted': 'Безкоштовна консультація',
    'nav.home': 'Головна',
    'nav.services': 'Ціни',
    'nav.referral': 'Реферальна програма',
    'nav.privacy': 'Політика конфіденційності',
    'nav.terms': 'Умови використання',
    // Cookie Banner
    'cookie.title': 'Ми використовуємо cookies',
    'cookie.description': 'Ми використовуємо cookies для покращення вашого досвіду на сайті, аналізу трафіку та персоналізації контенту.',
    'cookie.accept': 'Прийняти все',
    'cookie.decline': 'Відхилити',
    'cookie.learnMore': 'Детальніше',
    // Hero
    'hero.badge': 'Професійна IT-студія',
    'hero.title': 'Створюємо сучасні сайти',
    'hero.titleHighlight': 'для вашого бізнесу',
    'hero.subtitle': 'Допомагаємо бізнесу запускати сучасні сайти з продуманою структурою, зрозумілою подачею та акцентом на результат.',
    'hero.cta1': 'Вартість послуг',
    'hero.cta2': 'Замовити консультацію',
    'hero.referral': 'Реферальна програма',
    'hero.stat1.value': '7+',
    'hero.stat1.suffix': '',
    'hero.stat1.label': 'років створюємо it рішення',
    'hero.stat2.value': '499+',
    'hero.stat2.suffix': '',
    'hero.stat2.label': 'сайтів зроблено',
    'hero.stat3.value': '99.9%',
    'hero.stat3.suffix': '',
    'hero.stat3.label': 'позитивних відгуків',
    // Pricing
    'pricing.title': 'Вартість',
    'pricing.titleHighlight': 'створення сайту',
    'pricing.subtitle': 'Оберіть формат сайту під задачі вашого бізнесу. Допоможемо визначити відповідне рішення, структуру та наповнення проєкту.',
    'pricing.demo.title': 'Попередня концепція',
    'pricing.demo.price': '$0',
    'pricing.demo.label': 'БЕЗКОШТОВНО',
    'pricing.demo.desc': 'Обговоримо ваш проєкт, підкажемо оптимальний формат сайту та підготуємо попередній макет вашого сайту.',
    'pricing.demo.b1': 'Аналіз задачі та формату сайту',
    'pricing.demo.b2': 'Рекомендації щодо структури та подачі',
    'pricing.demo.b3': 'Безкоштовний макет',
    'pricing.demo.cta': 'Обговорити проєкт',
    'pricing.single.title': 'Односторінковий сайт',
    'pricing.single.price': 'від $590',
    'pricing.single.period': '',
    'pricing.single.desc': 'Сучасний односторінковий сайт для послуг, презентації компанії, особистого бренду або запуску реклами.',
    'pricing.single.b1': 'Односторінковий сайт',
    'pricing.single.b2': 'Індивідуальний дизайн',
    'pricing.single.b3': 'Зручна навігація та логічна структура',
    'pricing.single.b4': 'SEO оптимізація',
    'pricing.single.cta': 'Обговорити проєкт',
    'pricing.multi.title': 'Багатосторінковий сайт',
    'pricing.multi.price': 'від $790',
    'pricing.multi.period': '',
    'pricing.multi.desc': 'Повноцінний сайт для компаній, яким важливо представити послуги, посилити довіру та вибудувати зручну структуру для клієнтів.',
    'pricing.multi.b1': 'Багатосторінковий сайт',
    'pricing.multi.b2': 'Індивідуальний дизайн',
    'pricing.multi.b3': 'Зручна навігація та логічна структура',
    'pricing.multi.b4': 'SEO оптимізація',
    'pricing.multi.b5': '',
    'pricing.multi.cta': 'Обговорити проєкт',
    'pricing.note': '',
    // How It Works
    'how.title': 'Як ми створюємо',
    'how.titleHighlight': 'сайти?',
    'how.step1.title': 'Розкажіть про ваш бізнес',
    'how.step1.note': 'Вивчаємо ваші задачі, послуги, аудиторію та розуміємо, який сайт підійде саме вашому проєкту',
    'how.step2.title': 'Ми пропонуємо оптимальне рішення',
    'how.step2.note': 'Підкажемо відповідний формат сайту, структуру, подачу та функціонал з урахуванням ваших цілей та бюджету',
    'how.step3.title': 'Узгоджуємо концепцію та деталі',
    'how.step3.note': 'Обговорюємо стиль, блоки, сторінки та важливі елементи. За необхідності можемо підготувати безкоштовний макет',
    'how.step4.title': 'Створюємо сайт для вас',
    'how.step4.note': 'Після узгодження починаємо роботу над проєктом і доводимо сайт до готового результату',
    'how.step5.title': 'Підключаємо домен і публікуємо сайт',
    'how.step5.note': 'Сайт починає працювати та приймати заявки',
    'how.note': '',
    // Why Choose Us
    'why.title': 'Чому обирають нас?',
    'why.item1.title': 'Сучасні рішення',
    'why.item1.desc': 'Створюємо сайти з актуальною візуальною подачею, продуманою структурою та зручним інтерфейсом для вашого бізнесу і клієнтів.',
    'why.item2.title': 'Індивідуальний підхід',
    'why.item2.desc': 'Враховуємо особливості вашого бізнесу, задачі проєкту та бажаний стиль, щоб сайт вийшов точним і доречним.',
    'why.item3.title': 'Гарантія результату',
    'why.item3.desc': 'Працюємо уважно на кожному етапі проєкту, вносимо необхідні правки та доводимо сайт до результату, який вас повністю влаштує.',
    // FAQ
    'faq.title': 'Поширені',
    'faq.titleHighlight': 'запитання',
    'faq.subtitle': 'Все, що потрібно знати про роботу з нами',
    'faq.q1': 'З чого починається робота над проєктом?',
    'faq.a1': 'Спочатку ми вивчаємо ваш бізнес, задачі та побажання до сайту. Після цього пропонуємо оптимальний формат, структуру та підхід до реалізації проєкту.',
    'faq.q2': 'Які сайти ви створюєте?',
    'faq.a2': 'Ми розробляємо односторінкові та багатосторінкові сайти для компаній, спеціалістів, послуг та брендів. Підбираємо рішення під формат бізнесу, цілі та аудиторію.',
    'faq.q3': 'Чи допомагаєте ви визначити, який сайт потрібен?',
    'faq.a3': 'Так. Якщо у вас поки немає точного розуміння структури або формату, ми підкажемо, яке рішення підійде найкраще з точки зору подачі, зручності та задач бізнесу.',
    'faq.q4': 'Чи можна вносити правки в процесі?',
    'faq.a4': 'Так, у процесі роботи ми узгоджуємо важливі етапи та вносимо правки щодо структури, текстів, блоків та візуальної подачі в рамках проєкту.',
    'faq.q5': 'Ви працюєте з клієнтами по всьому світу?',
    'faq.a5': 'Так. Ми працюємо дистанційно та супроводжуємо проєкти для клієнтів з різних країн — від першого обговорення до передачі готового сайту.',
    // Testimonials
    'testimonials.title': 'Відгуки',
    'testimonials.titleHighlight': '',
    'testimonials.subtitle': 'Відгуки клієнтів, яким ми допомогли запустити сучасний сайт для бізнесу',
    'testimonials.r1.name': 'Оксана',
    'testimonials.r1.text': 'Сподобалося, що команда не просто робить дизайн, а дійсно допомагає розібратися, який сайт потрібен бізнесу. Все пояснили по структурі, запропонували зрозуміле рішення і акуратно реалізували проєкт.',
    'testimonials.r1.business': 'Майстер манікюру',
    'testimonials.r2.name': 'Віталій',
    'testimonials.r2.text': 'Було відчуття, що ми працюємо не просто з виконавцями, а з людьми, які розуміють подачу, логіку і довіру з боку клієнта. Сайт вийшов сучасним і зрозумілим.',
    'testimonials.r2.business': 'Ремонт техніки',
    'testimonials.r3.name': 'Андрій',
    'testimonials.r3.text': 'Сильна сторона команди — у підході. Нам допомогли не тільки з візуальною частиною, але і з загальною концепцією сайту, структурою і тим, як краще показати послуги.',
    'testimonials.r3.business': 'Юрист',
    // UA Consultation
    'consultation.title': 'Безкоштовна консультація',
    'consultation.titleHighlight': '',
    'consultation.subtitle': 'Розкажіть про ваш бізнес і задачі — ми підкажемо, який сайт підійде саме вам, запропонуємо структуру, формат і оптимальне рішення під ваш проект.',
    'consultation.bottomNote': "Після заявки ми зв'яжемося з вами та обговоримо оптимальний варіант запуску проекту.",
    'consultation.benefit1': 'Безкоштовний перегляд',
    'consultation.benefit2': 'Швидка доставка',
    'consultation.benefit3': "Без зобов'язань",
    'consultation.name': "Ім'я",
    'consultation.namePlaceholder': "Ваше ім'я",
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'ваш@email.com',
    'consultation.desc': 'Опис проєкту',
    'consultation.descPlaceholder': 'Розкажіть про ваш бізнес і який сайт вам потрібен...',
    'consultation.cta': 'Відправити заявку',
    'consultation.note': "Без зобов'язань. Якщо сайт не сподобається — ви не платите.",
    'consultation.sending': 'Відправка...',
    'consultation.or': 'або напишіть нам напряму',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Послуги',
    'footer.company': 'Компанія',
    'footer.legal': 'Юридичне',
    'footer.contact': 'Контакти',
    'footer.rights': 'Всі права захищені.',
    'footer.aboutLink': 'Про нас',
    // Stats
    'stats.websites': 'Сайтів запущено',
    'stats.clients': 'Задоволених клієнтів',
    'stats.years': 'Років досвіду',
    'stats.title': 'Наш',
    'stats.titleHighlight': 'Вплив',
    'stats.subtitle': 'Результати для бізнесу по всьому світу',
    // DD Page
    'dd.hero.title': 'IT Monsters',
    'dd.hero.titleHighlight': 'Діджитал Студія',
    'dd.hero.subtitle': 'Створюємо сайти, розробляємо додатки та запускаємо рекламу. Повний цикл digital-послуг для зростання вашого бізнесу.',
    'dd.hero.cta': 'Безкоштовна консультація',
    'dd.services.title': 'Наші',
    'dd.services.titleHighlight': 'Послуги',
    'dd.services.websites.title': 'Розробка сайтів',
    'dd.services.websites.desc': 'Сучасні адаптивні сайти з продуманою структурою, індивідуальним дизайном та SEO-оптимізацією для вашого бізнесу.',
    'dd.services.websites.cta': 'Детальніше',
    'dd.services.apps.title': 'Розробка додатків',
    'dd.services.apps.desc': 'Мобільні та веб-додатки з інтуїтивним інтерфейсом, надійною архітектурою та зручним користувацьким досвідом.',
    'dd.services.apps.cta': 'Детальніше',
    'dd.services.ads.title': 'Запуск реклами',
    'dd.services.ads.desc': 'Таргетована реклама в Google, Facebook та Instagram для залучення клієнтів та зростання вашого бізнесу.',
    'dd.services.ads.cta': 'Детальніше',
    'dd.faq.title': 'Чому обирають',
    'dd.faq.titleHighlight': 'нашу студію?',
    'dd.faq.q1': 'Чим IT Monsters краще за інші студії?',
    'dd.faq.a1': 'Ми поєднуємо глибоку технічну експертизу з сильним фокусом на дизайн та бізнес-результати. Кожен проєкт отримує індивідуальну увагу — від концепції до запуску.',
    'dd.faq.q2': 'Чим відрізняється ваш підхід?',
    'dd.faq.a2': 'Ми не просто виконуємо завдання — ми допомагаємо знайти найкраще рішення. Аналізуємо ваш бізнес, аудиторію та цілі до початку роботи.',
    'dd.faq.q3': 'Чи працюєте ви зі стартапами та малим бізнесом?',
    'dd.faq.a3': 'Так. Ми працюємо з бізнесом будь-якого масштабу — від індивідуальних підприємців до великих компаній. Адаптуємо підхід та бюджет під ваші потреби.',
    'dd.faq.q4': 'Які технології ви використовуєте?',
    'dd.faq.a4': 'Ми використовуємо сучасні технології: React, TypeScript, Node.js та інші. Обираємо найкращі інструменти для кожного конкретного проєкту.',
    'dd.faq.q5': 'Скільки часу займає типовий проєкт?',
    'dd.faq.a5': 'Односторінковий сайт — 1–2 тижні, багатосторінковий — 2–4 тижні, додатки — 4–8 тижнів залежно від складності. Терміни завжди узгоджуємо заздалегідь.'
  }
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/ru')) {
      setLanguage('ru');
    } else if (path.startsWith('/ua') || path.startsWith('/uk')) {
      setLanguage('ua');
    } else {
      setLanguage('en');
    }
  }, []);
  const t = (key: string): string => {
    const value = translations[language][key as keyof (typeof translations)['en']];
    return value !== undefined ? value : key;
  };
  const getPath = (path: string): string => {
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    if (language === 'ru') {
      return `/ru/${cleanPath}`;
    } else if (language === 'ua') {
      return `/ua/${cleanPath}`;
    }
    return `/${cleanPath}`;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    getPath
  }} data-id="element-413">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}